import React, { useState } from 'react';
import { getPaletteEntry } from './statusPalette';

// Task 28 — Peer fan-out renderer for consult_peers tool calls.
//
// Density rule (spec § 3.2 example 2): peer sub-cards render
// collapsed-by-default with a 2-line preview when peer_count > 2 OR
// any response exceeds 200 tokens. A group-level [expand all N peers]
// affordance lives at the bottom of the parent pill so the operator
// can flood-expand without clicking each row.

interface PeerEntry {
  // Backend canonical fields (backend/app/tools/consult_peers_tool.py:749)
  peer_persona_id?: string;
  peer_persona_name?: string;
  // Legacy/alt fields kept for defensive fallback
  persona_id?: string;
  persona_name?: string;
  name?: string;
  status?: string;          // ok | timeout | error | offline | ...
  duration_ms?: number;
  // `response` from LLM edge cases can be non-string (null, or a
  // content-block array). `any` lets us coerce with a typeof-guard
  // at the render site without lying about the shape.
  response?: any;
  content?: any;
  error?: string | null;
}

// Coerce LLM content to a safe string regardless of shape (string,
// null, content-block array, or unexpected object).
function peerBody(peer: PeerEntry): string {
  const raw = peer.response ?? peer.content;
  if (typeof raw === 'string') return raw;
  if (raw == null) return '';
  if (Array.isArray(raw)) {
    return raw
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object' && typeof block.text === 'string') {
          return block.text;
        }
        return '';
      })
      .join('');
  }
  return '';
}

interface ConsultPeersResult {
  peer_count?: number;
  ok_count?: number;
  offline_count?: number;
  responses?: PeerEntry[];  // canonical backend field
  peers?: PeerEntry[];      // defensive fallback
  results?: PeerEntry[];    // older shape
}

interface Props {
  result: ConsultPeersResult;
}

function estimateTokens(text: string | undefined | null): number {
  if (!text || typeof text !== 'string') return 0;
  // Coarse chars/4 estimate — matches backend fallback; good enough
  // for the "any response > 200 tokens" density gate.
  return Math.ceil(text.length / 4);
}

function formatDuration(ms: number | undefined): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

const PeerRow: React.FC<{
  peer: PeerEntry;
  forceExpanded: boolean;
  defaultCollapsed: boolean;
}> = ({ peer, forceExpanded, defaultCollapsed }) => {
  const [localExpanded, setLocalExpanded] = useState(!defaultCollapsed);
  const expanded = forceExpanded || localExpanded;
  const name = peer.peer_persona_name ?? peer.persona_name ?? peer.name ?? 'Unknown peer';
  const status = peer.status ?? 'ok';
  const palette = getPaletteEntry(status);
  const body = peerBody(peer);
  const previewLines = body.split('\n').slice(0, 2).join('\n');
  const hasBody = body.length > 0;

  return (
    <div className="pl-3 border-l border-gray-500/30 my-1">
      <button
        type="button"
        onClick={() => setLocalExpanded((v) => !v)}
        className="w-full text-left flex items-center gap-1.5 text-[11px] font-mono opacity-90 hover:opacity-100"
        aria-expanded={expanded}
      >
        <span className={palette.color}>{palette.icon}</span>
        <span className="truncate max-w-[16rem]">{name}</span>
        {peer.duration_ms != null && (
          <span className="opacity-60">({formatDuration(peer.duration_ms)})</span>
        )}
        {palette.label && status !== 'ok' && (
          <span className={`opacity-80 ${palette.color}`}>· {palette.label}</span>
        )}
        {hasBody && (
          <span className="opacity-40 ml-auto">{expanded ? '▾' : '▸'}</span>
        )}
      </button>
      {hasBody && (
        <div
          className={`text-[11px] mt-0.5 whitespace-pre-wrap leading-snug opacity-90 ${
            expanded ? '' : 'line-clamp-2'
          }`}
          style={expanded ? undefined : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {expanded ? body : previewLines}
        </div>
      )}
      {peer.error && status !== 'ok' && (
        <div className="text-[10px] italic opacity-70 mt-0.5">{peer.error}</div>
      )}
    </div>
  );
};

export const ConsultPeersSubCard: React.FC<Props> = ({ result }) => {
  // Backend canonical field is `responses` (see
  // backend/app/tools/consult_peers_tool.py). peers/results are
  // defensive fallbacks for legacy persisted entries only.
  const peers =
    (result as any).responses ?? result.peers ?? result.results ?? [];
  const hasLongResponse = peers.some((p) => estimateTokens(p.response ?? p.content) > 200);
  const densityCollapse = peers.length > 2 || hasLongResponse;
  const [expandAll, setExpandAll] = useState(!densityCollapse);

  if (peers.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-col">
      {peers.map((peer, i) => (
        <PeerRow
          key={peer.persona_id ?? `${i}-${peer.persona_name ?? peer.name ?? 'peer'}`}
          peer={peer}
          forceExpanded={expandAll}
          defaultCollapsed={densityCollapse}
        />
      ))}
      {densityCollapse && (
        <button
          type="button"
          onClick={() => setExpandAll((v) => !v)}
          className="self-start mt-1 text-[10px] font-mono opacity-60 hover:opacity-100 underline"
        >
          {expandAll ? `[collapse all ${peers.length} peers]` : `[expand all ${peers.length} peers]`}
        </button>
      )}
    </div>
  );
};

export default ConsultPeersSubCard;
